export interface SieveQuery {
  filters?: string;
  sorts?: string;
  page?: number;
  pageSize?: number;
  version?: string;
}
/**
 * Sieve Query Builder - Type-safe construction of Sieve filter and sort expressions
 * Based on Biarity/Sieve library for .NET Core
 * 
 * Usage:
 *   const query = new SieveQueryBuilder()
 *     .filter('name', '==', 'john')
 *     .filter('age', '>', 25)
 *     .sort('createdAt', 'desc')
 *     .page(1)
 *     .pageSize(10)
 *     .build();
 */

export type SieveOperator =
  | '==' | '!='
  | '>' | '<' | '>=' | '<='
  | '@=' | '!@='
  | '@=*' | '!@=*'
  | '_=' | '!_='
  | '_=*' | '!_=*'
  | '_-=' | '!_-=' | '_-=*'
  | '==*' | '!=*'
  | '|=' | '!|='
  | '~='
  | '@!*'
  | 'is_null' | 'is_not_null';


export type SortDirection = 'asc' | 'desc';

export interface SieveFilter {
  property: string;
  operator: SieveOperator;
  value: string | number | boolean;
}

export interface SieveSort {
  property: string;
  direction: SortDirection;
}

export interface SieveQuery {
  filters?: string;
  sorts?: string;
  page?: number;
  pageSize?: number;
}

export class SieveQueryBuilder {
  private _filters: SieveFilter[] = [];
  private _sorts: SieveSort[] = [];
  private _page?: number;
  private _pageSize?: number;

  /**
   * Add a filter condition
   * @param property Property name to filter on
   * @param operator Sieve operator (==, !=, >, <, etc.)
   * @param value Value to filter by
   */
  filter(property: string, operator: SieveOperator, value: string | number | boolean): this {
    this._filters.push({ property, operator, value });
    return this;
  }

  /**
   * Add multiple filters with OR logic
   * @param property Property name to filter on
   * @param operator Sieve operator
   * @param values Array of values for OR condition
   */
  filterOr(property: string, operator: SieveOperator, values: (string | number | boolean)[]): this {
    if (values.length === 0) return this;
    if (values.length === 1) {
      return this.filter(property, operator, values[0]);
    }

    // For OR logic, join values with |
    const orValue = values.map(v => String(v)).join('|');
    this._filters.push({ property, operator, value: orValue });
    return this;
  }

  /**
   * Add a text search filter (contains)
   * @param property Property name to search in
   * @param value Text to search for
   */
  search(property: string, value: string): this {
    if (value.trim()) {
      return this.filter(property, '@=*', value.trim());
    }
    return this;
  }

  /**
   * Add a sort condition
   * @param property Property name to sort by
   * @param direction Sort direction ('asc' or 'desc')
   */
  sort(property: string, direction: SortDirection = 'asc'): this {
    this._sorts.push({ property, direction });
    return this;
  }

  /**
   * Set page number for pagination
   * @param page Page number (1-based)
   */
  page(page: number): this {
    this._page = page > 0 ? page : 1;
    return this;
  }

  /**
   * Set page size for pagination
   * @param pageSize Number of items per page
   */
  pageSize(pageSize: number): this {
    this._pageSize = pageSize > 0 ? pageSize : 10;
    return this;
  }

  /**
   * Clear all filters
   */
  clearFilters(): this {
    this._filters = [];
    return this;
  }

  /**
   * Clear all sorts
   */
  clearSorts(): this {
    this._sorts = [];
    return this;
  }

  /**
   * Reset all query parameters
   */
  reset(): this {
    this._filters = [];
    this._sorts = [];
    this._page = undefined;
    this._pageSize = undefined;
    return this;
  }

  /**
   * Build the final Sieve query object
   */
  build(): SieveQuery {
    const query: SieveQuery = {};

    // Build filters string
    if (this._filters.length > 0) {
      query.filters = this._filters
        .map(f => `${f.property}${f.operator}${f.value}`)
        .join(',');
    }

    // Build sorts string
    if (this._sorts.length > 0) {
      query.sorts = this._sorts
        .map(s => s.direction === 'desc' ? `-${s.property}` : s.property)
        .join(',');
    }

    // Add pagination
    if (this._page !== undefined) {
      query.page = this._page;
    }
    if (this._pageSize !== undefined) {
      query.pageSize = this._pageSize;
    }

    return query;
  }

  /**
   * Build and return query parameters as URLSearchParams
   */
  buildParams(): URLSearchParams {
    const query = this.build();
    const params = new URLSearchParams();

    if (query.filters) params.set('filters', query.filters);
    if (query.sorts) params.set('sorts', query.sorts);
    if (query.page) params.set('page', query.page.toString());
    if (query.pageSize) params.set('pageSize', query.pageSize.toString());

    return params;
  }

  /**
   * Build and return query parameters as plain object
   */
  buildQueryParams(): Record<string, string> {
    const query = this.build();
    const params: Record<string, string> = {};

    if (query.filters) params.filters = query.filters;
    if (query.sorts) params.sorts = query.sorts;
    if (query.page) params.page = query.page.toString();
    if (query.pageSize) params.pageSize = query.pageSize.toString();

    return params;
  }
}

/**
 * Utility function to create a new SieveQueryBuilder
 */
export function sieve(): SieveQueryBuilder {
  return new SieveQueryBuilder();
}

/**
 * Common filter builders for frequently used patterns
 */
export const SieveFilters = {
  /**
   * Create an active/inactive filter
   */
  isActive: (active: boolean) => sieve().filter('isActive', '==', active),

  /**
   * Create a text search across multiple properties with AND logic
   */
  search: (properties: string[], searchTerm: string) => {
    const builder = sieve();
    properties.forEach(prop => {
      builder.filter(prop, '@=*', searchTerm);
    });
    return builder;
  },

  /**
   * Create a date range filter
   */
  dateRange: (property: string, from?: Date, to?: Date) => {
    const builder = sieve();
    if (from) {
      builder.filter(property, '>=', from.toISOString());
    }
    if (to) {
      builder.filter(property, '<=', to.toISOString());
    }
    return builder;
  },

  /**
   * Create an 'in' filter for multiple values
   */
  in: <T>(property: string, values: T[]) => {
    return sieve().filterOr(property, '==', values as (string | number | boolean)[]);
  }
};
